import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { SessionService } from '../../services/session.service';
import { PlatformApiService } from '../../services/platform-api.service';
import { Account, AccountType } from '../../models/AccountModel';
import { AccountAssessment } from 'src/app/models/AssessmentModel';
import { ThemeManagerService } from 'src/app/services/theme-manager.service';
import { saveAs } from 'file-saver';
import { GridSpinnerComponent } from 'src/app/shared/grid-spinner.component';
import { ConfirmationComponent } from 'src/app/shared/confirmation.component';
import { MatDialog } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { SettingsWizardComponent } from '../settings-wizard/settings-wizard-dialog.component';
import { SubAccountSummaryListComponent } from '../account/sub-account-summary-list.component';
import { ScanTarget } from 'src/app/models/ScanTargetModel';
import { ScanResult } from 'src/app/models/ScanResultModel';

@Component({
  selector: 'cw-ci-sub-account-dashboard',
  templateUrl: './sub-account-dashboard.component.html',
  styleUrls: []
})
export class SubAccountDashboardComponent implements OnInit, OnDestroy {
  allowAllCompanies = true;
  state='LIST';
  selectedAccount: Account;
  selectedAssessment: AccountAssessment;
  selectedScanTarget: ScanTarget;
  selectedScanResult: ScanResult;
  parentAccount: Account;

  public destroyed = new Subject<any>();

  @ViewChild(SubAccountSummaryListComponent) accountListComponent: SubAccountSummaryListComponent;
  @ViewChild('spinner', { static: true }) spinner: GridSpinnerComponent;

  constructor(private _activatedRoute: ActivatedRoute, private _platformApi: PlatformApiService, public _sessionService: SessionService, private _themeManager: ThemeManagerService, public dialog: MatDialog, private _router: Router) {

    if (this._sessionService.getCurrentAccount().type != AccountType.SYSTEM) {
      this.parentAccount = this._sessionService.getCurrentAccount();
    }
  }

  ngOnInit(): void {
    if (this._activatedRoute.snapshot.data.allowAllCompanies === false) {
      this.allowAllCompanies = false;
    };

    this._activatedRoute.params.subscribe(params => {
      console.log('SubAccountDashboardComponent:ngOnInit:activatedRoute', this._activatedRoute.routeConfig.path, params);
      const requestedAccount = params.subAccountId;
      const requestedAssessment = params.assessmentId;
      const requestedScanTarget = params.scanTargetId;
      const requestedScanResult = params.scanResultId;

      const lastRouteSnapshot = this._activatedRoute.snapshot.pathFromRoot[this._activatedRoute.snapshot.pathFromRoot.length - 1];
      const lastUrlSegment = lastRouteSnapshot.url[lastRouteSnapshot.url.length - 1];

      if (requestedAccount) {
        this.state = undefined;
        this._platformApi.getSubAccount(this.parentAccount.id, requestedAccount).subscribe(acct => {
          this.selectedAccount = acct;

          if (requestedAssessment) {
            this._platformApi.getAccountAssessment(this.parentAccount.id, this.selectedAccount.id, requestedAssessment).subscribe(aa => {
              this.selectedAssessment = aa;
              if (this.selectedAssessment.status == 'COMPLETED') {
                this.state = 'ASSESSMENT_REPORT';
              } else {
                this.state = 'EDIT_ASSESSMENT';
              }
            });
          } else if (requestedScanTarget) {
            let parentAcctId;
            let subAcctId;
            if (this.parentAccount) {
              parentAcctId = this.parentAccount.id;
              subAcctId = this.selectedAccount.id;
            } else {
              parentAcctId = this.selectedAccount.id;
            }
            this._platformApi.getScanTarget(parentAcctId, subAcctId, requestedScanTarget).subscribe(st => {
              this.selectedScanTarget = st;

              if (requestedScanResult) {
                this._platformApi.getScanTargetResult(parentAcctId, subAcctId, this.selectedScanTarget.id, requestedScanResult).subscribe(sr => {
                  this.selectedScanResult = sr;
                  this.state = 'SCAN_TARGET_RESULT';
                });

              } else {
                this.state = 'SCAN_TARGET_RESULT_LIST';
              }
            });
          } else if (lastUrlSegment.path == 'scanTargets') {
            this.state = 'SCAN_TARGET_LIST';
          } else {
            this.state = 'ASSESSMENT_LIST';
          }
        });
      }
    });
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  showAllCompanies() {
    console.log('SubAccountDashboardComponent:showAllCompanies:route', `companies`, {relativeTo:this._activatedRoute.parent});
    this._router.navigate([`companies`], {relativeTo:this._activatedRoute.parent});
  }

  showCompany() {
    if (this.selectedScanTarget) {
      this.showCompanyScanTargets();
    } else {
      console.log('SubAccountDashboardComponent:showCompany:route', `companies/${this.selectedAccount.id}`, {relativeTo:this._activatedRoute.parent});
      this._router.navigate([`companies/${this.selectedAccount.id}`], {relativeTo:this._activatedRoute.parent});
    }
  }

  deleteAccount(account) {
    this._platformApi.deleteAccount(account.id).subscribe(
      resp => {
        this.accountListComponent.loadAccounts();
      }
    );
  }

  showCompanyScanTargets() {
    console.log('SubAccountDashboardComponent:showAllScanTargets:route', `scanTargets`, {relativeTo:this._activatedRoute.parent});
    this._router.navigate([`companies/${this.selectedAccount.id}/scanTargets`], {relativeTo:this._activatedRoute.parent});
  }

  showScanTargetResults() {
    console.log('SubAccountDashboardComponent:showScanTargetResults:route', `scanResults`, {relativeTo:this._activatedRoute.parent});
    this._router.navigate([`companies/${this.selectedScanTarget.accountId}/scanTargets/${this.selectedScanTarget.id}/scanResults`], {relativeTo:this._activatedRoute.parent});
  }

  showScanTargetResult() {
    console.log('SubAccountDashboardComponent:showScanTargetResult:route', `scanResults/${this.selectedScanResult.id}`, {relativeTo:this._activatedRoute.parent});
    this._router.navigate([`companies/${this.selectedScanTarget.accountId}/scanTargets/${this.selectedScanTarget.id}/scanResults/${this.selectedScanResult.id}`], {relativeTo:this._activatedRoute.parent});
  }

  onStateChange(event) {
    console.log('SubAccountDashboardComponent:onStateChange:event', event);
    let dialogRef;
    switch(event.state) {
      case 'CREATE':
        const newAccount = new Account({type: AccountType.COMPANY});
        if (this._sessionService.getCurrentAccount().type != AccountType.SYSTEM) {
          const currentAccount = this._sessionService.getCurrentAccount()
          newAccount.parentId = currentAccount.id;
          newAccount.masterPlanSubscription = currentAccount.planSubscription;
        }
        dialogRef = this.dialog.open(SettingsWizardComponent, {
          panelClass: 'flush-dialog',
          disableClose: true,
          autoFocus: false,
          data: {
            selectedAccount: newAccount,
            startingMenuItem: 'ACCOUNT_START'
          }
        });
        dialogRef.afterClosed().subscribe(() => {
          this.accountListComponent.refresh();
        });
        break;
      case 'SELECT':
      case 'EDIT':
        dialogRef = this.dialog.open(SettingsWizardComponent, {
          panelClass: 'flush-dialog',
          disableClose: true,
          autoFocus: false,
          data: {
            selectedAccount: event.item,
            startingMenuItem: 'ACCOUNT_START'
          }
        });
        dialogRef.afterClosed().subscribe(() => {
          this.accountListComponent.refresh();
        });
        break;
      case 'DELETE':
        this.deleteAccount(event.item);
        break;
      case 'CREATESUCCESS':
      case 'EDITSUCCESS':
      case 'CREATECANCEL':
      case 'EDITCANCEL':
        this.selectedAccount = undefined;
        this.state = 'LIST';
        break;
      case 'ASSESSMENT_LIST':
        this.selectedAccount = event.item;
        this.showCompany();
        break;
      case 'ASSESSMENT_REPORT':
      case 'EDIT_ASSESSMENT':
        this.selectedAccount = event.account;
        if (event.accountAssessment == undefined) {
          this._platformApi.getAccountAssessment(this.parentAccount.id, this.selectedAccount.id, this.selectedAccount.summary.recentAssessmentId).subscribe(aa => {
            this.selectedAssessment = aa;
            console.log('SubAccountDashboardComponent:onStateChange:subAccountAssessmentNavigate', `companies/${this.selectedAccount.id}/assessment/${this.selectedAssessment.id}`, {relativeTo: this._activatedRoute.parent})
            this._router.navigate([`companies/${this.selectedAccount.id}/assessment/${this.selectedAssessment.id}`], {relativeTo: this._activatedRoute.parent});
          })
        } else {
          this.selectedAssessment = event.accountAssessment;
          console.log('SubAccountDashboardComponent:onStateChange:assessmentNavigate', `companies/${this.selectedAccount.id}/assessment/${this.selectedAssessment.id}`, {relativeTo: this._activatedRoute.parent})
          this._router.navigate([`companies/${this.selectedAccount.id}/assessment/${this.selectedAssessment.id}`], {relativeTo: this._activatedRoute.parent});
        }
        break;
      case 'COMPLETEDASSESSMENT':
        this.selectedAssessment = event.completedAssessment;
        this.state = event.state;
        break;
      case 'SHOW_SCAN_TARGETS':
        this.selectedAccount = event.item;
        this.showCompanyScanTargets();
        break;
      case 'SELECT_SCAN_TARGET':
        this.selectedScanTarget = event.item;
        this.showScanTargetResults();
        break;
      case 'SELECT_SCAN_TARGET_RESULT':
        this.selectedScanResult = event.item;
        this.showScanTargetResult();
        break;
      }
  }

  getAssessmentReport() {
    const includeAttestation = (this._sessionService.getCurrentAccount().id != this.selectedAssessment.accountId && this._sessionService.hasFeature('INCLUDE_CLIENT_ATTESTATION')) ? true : false;

    if (includeAttestation) {
      const dialogRef = this.dialog.open(ConfirmationComponent, {
        panelClass: 'flush-dialog',
        data: {
          promptTitle: 'Include Attestation?',
          promptMessage: [
            'Do you want to include the attestation page?'
          ],
          closeLabel: 'Cancel',
          buttons: [{label:'No', value:'NO', color:'primary'}, {label:'Yes', value:'YES', color:'primary'}],
          height: 'auto'
        }
      });
      dialogRef.afterClosed().subscribe(includeResponse => {
        if (includeResponse !== undefined) {
          this.downloadReport(includeResponse == 'YES');
        }
      });
    } else {
      this.downloadReport(false);
    }
  }

  downloadReport(includeAttestation:boolean) {
    const self = this;
    self.spinner.showSpinner('Generating report...');
    self._platformApi.getAssessmentReport(
      self._sessionService.getCurrentAccount().id,
      self._sessionService.getCurrentUser().firstName,
      self._sessionService.getCurrentAccount().name,
      self.selectedAssessment,
      includeAttestation,
      self._sessionService.hasFeature('INCLUDE_REPORT_ANALYTICS'),
      self._themeManager.getThemeClass()
    ).subscribe(
      data => {
        if (data) {
          const assessData = this.selectedAssessment
          const blob = new Blob([data], { type: 'application/pdf' });
          saveAs(blob, assessData.accountName+' Assessment Report '+assessData.updatedAt.toLocaleDateString()+'.pdf');
        }
        self.spinner.hideSpinner();
      },
      error => {
        console.log('Error while generating assessment report', error);
        self.spinner.showMessage('An error occurred while generating the report. Please contact an administrator', 5000);
      }
    );
  }
}